import { useEffect, useMemo, useReducer, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { dataSourceRoot, getDataSource } from '../../../data/dataSourceFactory';
import type { DicomJson } from '../../../data/dicomTags';
import type { LayerInfo, LayerStyle, ViewportEngine } from '../../../viewer/ViewportEngine';
import { runState } from '../states';
import type { Run, RunOutput } from '../types';
import type { OutputRendererProps } from './types';

type Rgb = [number, number, number];

/** Each mask of a run its own colour, in output order. */
export const MASK_COLORS: Rgb[] = [
  [34, 197, 94],
  [239, 68, 68],
  [59, 130, 246],
  [245, 158, 11],
  [168, 85, 247],
  [20, 184, 166],
  [236, 72, 153],
];
export const DEFAULT_OPACITY = 0.5;

interface MaskStyle {
  opacity: number;
  color: Rgb;
}

/** How each mask is drawn, by run and output, for the session: it outlives the tab and the slide. */
const styles = new Map<string, MaskStyle>();
/**
 * Which layers are on, per engine. A new engine (another slide, or this one
 * opened again) starts with every mask off, as its layers are new.
 */
const visible = new WeakMap<object, Set<string>>();

function isOn(engine: ViewportEngine, uid: string): boolean {
  return visible.get(engine)?.has(uid) ?? false;
}

function setOn(engine: ViewportEngine, uid: string, on: boolean): void {
  let set = visible.get(engine);
  if (!set) visible.set(engine, (set = new Set()));
  if (on) set.add(uid);
  else set.delete(uid);
}

export const toHex = (c: Rgb) => `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
export function fromHex(hex: string): Rgb | null {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
}

const norm = (s: string | null | undefined) => (s ?? '').trim().toLowerCase();

/**
 * Which layer draws each mask. Masks of one run may share one Segmentation
 * series, one segment each: matched by the output's `segmentNumber`, else by
 * the segment's label being the output's title.
 */
export function matchMaskLayers(
  outputs: RunOutput[],
  bySeries: Record<string, LayerInfo[] | undefined>,
): Record<string, LayerInfo | undefined> {
  const out: Record<string, LayerInfo | undefined> = {};
  for (const o of outputs) {
    const layers = o.seriesUid ? bySeries[o.seriesUid] : undefined;
    if (!layers?.length) continue;
    const byNumber = o.segmentNumber != null ? layers.find((l) => l.segmentNumber === Number(o.segmentNumber)) : undefined;
    out[o.key] = byNumber ?? layers.find((l) => norm(l.label) === norm(o.title));
  }
  return out;
}

/**
 * The run's masks on the slide: each Segmentation series' metadata read
 * through the data source (the cache the viewer's own metadata uses), handed
 * to the engine once the run is done, its pixels fetched ahead, and each mask
 * matched to its layer.
 */
function useMaskLayers(run: Run, outputs: RunOutput[], engine: ViewportEngine | null, studyUID: string | undefined) {
  const canDraw = !!engine?.addSegmentationLayer;
  const done = runState(run) === 'done';
  // Keyed by what the outputs name, not by the array: a run's outputs are a
  // new array at every render, and the effect below must not run at each.
  const seriesKey = outputs.map((o) => `${o.seriesUid ?? ''}|${o.studyUid ?? ''}`).join(',');
  const series = useMemo(() => {
    const seen = new Map<string, string | undefined>();
    for (const o of outputs) if (o.seriesUid && !seen.has(o.seriesUid)) seen.set(o.seriesUid, o.studyUid ?? studyUID);
    return [...seen].map(([seriesUid, studyUid]) => ({ seriesUid, studyUid }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesKey, studyUID]);
  const metas = useQueries({
    queries: series.map((s) => ({
      queryKey: ['series-metadata', dataSourceRoot(), s.studyUid, s.seriesUid],
      queryFn: () => getDataSource().retrieveSeriesMetadata(s.studyUid!, s.seriesUid),
      enabled: canDraw && done && !!s.studyUid,
      staleTime: Infinity,
    })),
  });
  const [bySeries, setBySeries] = useState<{ engine: ViewportEngine | null; layers: Record<string, LayerInfo[]> }>({ engine, layers: {} });
  const [failed, setFailed] = useState(false);
  const ready = metas.map((m) => (m.data ? 1 : 0)).join('');
  useEffect(() => {
    if (!engine?.addSegmentationLayer) return;
    let live = true;
    setFailed(false);
    series.forEach((s, i) => {
      const data = metas[i]?.data as DicomJson[] | undefined;
      if (!data) return;
      engine.addSegmentationLayer!(data).then(
        (infos) => {
          // Superseded (another image, other outputs): the run that replaced
          // this one adds the series again and fetches for itself.
          if (!live) return;
          // The masks' pixels now, while they are only listed: switching one
          // on then draws at once. Asked again, it fetches nothing new.
          engine.prefetchLayers?.(s.seriesUid);
          setBySeries((prev) =>
            prev.engine === engine && prev.layers[s.seriesUid] === infos
              ? prev
              : { engine, layers: { ...(prev.engine === engine ? prev.layers : {}), [s.seriesUid]: infos } },
          );
        },
        (err) => {
          if (!live) return;
          console.warn('Could not show the masks:', err);
          setFailed(true);
        },
      );
    });
    return () => {
      live = false;
    };
    // `ready` stands for the metadata having arrived.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, ready, series]);
  const layers = bySeries.engine === engine ? bySeries.layers : {};
  const metaFailed = metas.some((m) => m.isError);
  return { canDraw, done, matched: matchMaskLayers(outputs, layers), failed: failed || metaFailed };
}

/**
 * Masks: one row each — its colour, its name, its opacity, and at the right
 * whether it is shown — drawn by the engine's layers; above them, one switch
 * for all of them. What is shown lives in `visible`, per engine; a change
 * here re-renders the rows, which read it.
 */
export function MaskOutputs({ run, outputs, env }: OutputRendererProps) {
  const { canDraw, done, matched, failed } = useMaskLayers(run, outputs, env.engine, env.studyUID);
  const engine = env.engine;
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const uids = outputs.map((o) => matched[o.key]?.uid).filter((u): u is string => !!u);
  const allOn = !!engine && uids.length > 0 && uids.every((uid) => isOn(engine, uid));
  const show = (uid: string, next: boolean) => {
    if (!engine) return;
    setOn(engine, uid, next);
    if (next) engine.showLayer?.(uid);
    else engine.hideLayer?.(uid);
  };
  const showAll = (next: boolean) => {
    for (const uid of uids) show(uid, next);
    bump();
  };
  const note = !done
    ? null
    : !env.engine
      ? (env.noLayers ?? 'Masks show once the image is open.')
      : !canDraw
        ? 'This viewer cannot show masks.'
        : failed
          ? 'The masks could not be shown.'
          : null;
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">Masks</span>
        <Switch size="sm" label="All" ariaLabel="Show all masks" checked={allOn} onCheckedChange={showAll} disabled={!engine || uids.length === 0} />
      </div>
      {outputs.map((o, i) => {
        const uid = matched[o.key]?.uid;
        return (
          <MaskRow
            key={o.key}
            run={run}
            output={o}
            index={i}
            engine={engine}
            layer={matched[o.key]}
            on={!!(engine && uid && isOn(engine, uid))}
            onToggle={(next) => {
              if (uid) show(uid, next);
              bump();
            }}
          />
        );
      })}
      {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
    </div>
  );
}

function MaskRow({
  run,
  output,
  index,
  engine,
  layer,
  on,
  onToggle,
}: {
  run: Run;
  output: RunOutput;
  index: number;
  engine: ViewportEngine | null;
  layer: LayerInfo | undefined;
  /** Whether its layer is shown (MaskOutputs reads `visible`). */
  on: boolean;
  onToggle: (next: boolean) => void;
}) {
  const id = `${run.id}:${output.key}`;
  const title = output.title || 'Mask';
  const [style, setStyle] = useState<MaskStyle>(
    () => styles.get(id) ?? { opacity: DEFAULT_OPACITY, color: MASK_COLORS[index % MASK_COLORS.length] },
  );
  const uid = layer?.uid;
  // A layer that has just appeared (a run done, the slide opened again) is
  // drawn as this mask was last set: its colour and opacity, on or off.
  useEffect(() => {
    if (!engine || !uid) return;
    engine.setLayerStyle?.(uid, { opacity: style.opacity, color: style.color });
    if (isOn(engine, uid)) engine.showLayer?.(uid);
    else engine.hideLayer?.(uid);
    // The style is applied as it changes, below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, uid]);
  const disabled = !engine || !uid;

  const restyle = (patch: Partial<MaskStyle>) => {
    const next = { ...style, ...patch };
    styles.set(id, next);
    setStyle(next);
    if (engine && uid) engine.setLayerStyle?.(uid, patch as LayerStyle);
  };
  const hex = toHex(style.color);
  const pct = Math.round(style.opacity * 100);

  return (
    <div className="flex items-center gap-2 py-0.5" data-mask={output.key}>
      <Tooltip>
        <TooltipTrigger asChild>
          <label
            className="relative h-4 w-4 shrink-0 cursor-pointer rounded-full border border-black/40 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50"
            style={{ backgroundColor: hex }}
          >
            <input
              type="color"
              value={hex}
              disabled={disabled}
              aria-label={`${title} colour`}
              onChange={(e) => {
                const c = fromHex(e.target.value);
                if (c) restyle({ color: c });
              }}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
            />
          </label>
        </TooltipTrigger>
        <TooltipContent>Colour</TooltipContent>
      </Tooltip>
      <span className={cn('min-w-0 flex-1 truncate text-xs', disabled ? 'text-muted-foreground' : 'text-foreground')}>{title}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={pct}
            disabled={disabled}
            aria-label={`${title} opacity`}
            onChange={(e) => restyle({ opacity: Number(e.target.value) / 100 })}
            className="h-1.5 w-16 shrink-0 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-50"
          />
        </TooltipTrigger>
        <TooltipContent>Opacity {pct}%</TooltipContent>
      </Tooltip>
      <Switch size="sm" label="" ariaLabel={`Show ${title}`} checked={on} onCheckedChange={onToggle} disabled={disabled} />
    </div>
  );
}
